import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Plan, OrganizationQuotaDetails, Organization } from '../../../types/domain';
import { formatBillingDate } from '../types';
import { Check, ArrowDownRight, AlertTriangle } from 'lucide-react';

interface SubscriptionModalsProps {
  currentOrg: Organization | null;
  quota: OrganizationQuotaDetails | null;
  isMutating: boolean;
  upgradeTargetPlan: Plan | null;
  onCloseUpgrade: () => void;
  onConfirmUpgrade: () => void;
  downgradeTargetPlan: Plan | null;
  onCloseDowngrade: () => void;
  onConfirmDowngrade: () => void;
  cancelModalOpen: boolean;
  onCloseCancel: () => void;
  onConfirmCancel: () => void;
}

export const SubscriptionModals: React.FC<SubscriptionModalsProps> = ({
  currentOrg,
  quota,
  isMutating,
  upgradeTargetPlan,
  onCloseUpgrade,
  onConfirmUpgrade,
  downgradeTargetPlan,
  onCloseDowngrade,
  onConfirmDowngrade,
  cancelModalOpen,
  onCloseCancel,
  onConfirmCancel,
}) => {
  return (
    <>
      {/* Upgrade Confirmation Modal */}
      <Modal
        isOpen={Boolean(upgradeTargetPlan)}
        onClose={onCloseUpgrade}
        title={`Upgrade to ${upgradeTargetPlan?.name || 'Plan'}`}
      >
        <div className="space-y-4">
          <p className="text-xs text-cf-textSecondary">
            You are upgrading <strong className="text-cf-textPrimary">{currentOrg?.name}</strong> to the{' '}
            <strong className="text-cf-textPrimary">{upgradeTargetPlan?.name}</strong> plan at{' '}
            <strong className="text-cf-textPrimary">${((upgradeTargetPlan?.price_monthly_cents || 0) / 100).toFixed(0)}/month</strong>.
          </p>

          <div className="p-3 rounded bg-cf-bg border border-cf-border text-xs space-y-1.5">
            <div className="font-semibold text-cf-textPrimary">Instant Activation Highlights:</div>
            <ul className="text-cf-textSecondary space-y-1 pl-1">
              <li>• Quota expanded to {upgradeTargetPlan?.max_monthly_submissions.toLocaleString()} monthly submissions.</li>
              <li>• Capacity increased to {upgradeTargetPlan?.max_tables.toLocaleString()} tables and {upgradeTargetPlan?.max_team_seats.toLocaleString()} seats.</li>
              <li>• Monthly submission counter resets immediately to 0 for a fresh 30-day billing cycle.</li>
              <li>• Any pending downgrade or cancellation will be cleared immediately.</li>
            </ul>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onCloseUpgrade}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="xs"
              onClick={onConfirmUpgrade}
              disabled={isMutating}
              isLoading={isMutating}
              leftIcon={<Check size={13} />}
            >
              Confirm Upgrade
            </Button>
          </div>
        </div>
      </Modal>

      {/* Downgrade Warning Modal */}
      <Modal
        isOpen={Boolean(downgradeTargetPlan)}
        onClose={onCloseDowngrade}
        title={`Downgrade to ${downgradeTargetPlan?.name || 'Plan'}`}
      >
        <div className="space-y-4">
          <p className="text-xs text-cf-textSecondary">
            Your downgrade to <strong className="text-cf-textPrimary">{downgradeTargetPlan?.name}</strong> will take effect at the end of your current billing period on{' '}
            <strong className="text-cf-textPrimary">{formatBillingDate(quota?.current_period_end)}</strong>. You will retain all current plan features until then.
          </p>

          <div className="p-3 rounded bg-rose-500/10 border border-rose-500/30 text-xs text-rose-500 dark:text-rose-400 font-medium leading-relaxed">
            Before downgrading, ensure to backup your data, any data exceeding the new limits will be deleted in chronological order from oldest to newest.
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onCloseDowngrade}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              size="xs"
              onClick={onConfirmDowngrade}
              disabled={isMutating}
              isLoading={isMutating}
              leftIcon={<ArrowDownRight size={13} />}
            >
              Confirm Downgrade
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Subscription Modal */}
      <Modal
        isOpen={cancelModalOpen}
        onClose={onCloseCancel}
        title="Cancel Subscription"
      >
        <div className="space-y-4">
          <p className="text-xs text-cf-textSecondary">
            Your subscription will remain active until the end of your current cycle on{' '}
            <strong className="text-cf-textPrimary">{formatBillingDate(quota?.current_period_end)}</strong>. At that point, your organization will automatically downgrade to the Free tier.
          </p>

          <div className="p-3 rounded bg-rose-500/10 border border-rose-500/30 text-xs text-rose-500 dark:text-rose-400 font-medium leading-relaxed">
            Before downgrading, ensure to backup your data, any data exceeding the new limits will be deleted in chronological order from oldest to newest.
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onCloseCancel}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              size="xs"
              onClick={onConfirmCancel}
              disabled={isMutating}
              isLoading={isMutating}
              leftIcon={<AlertTriangle size={13} />}
            >
              Confirm Cancellation
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
